/**
 * Recipes compiled by recipe-builder (`created_by: "recipe_builder"`) are managed in git and published by its sync, so
 * the CRM must not edit their content: the editors below still speak an older recipe shape and damage current-schema
 * recipes on save (core#33 §3), and any edit would be overwritten by the next sync anyway.
 *
 * Guarded: the recipes-table "Edit" dialog (saveRecipe), the recipe view's "Edit Recipe" (saveUnifiedRecipe) and its
 * inline Taxonomy edit (saveTaxonomy). Recipes created in the CRM stay editable.
 */

export const RECIPE_BUILDER_CREATOR = "recipe_builder";

export const RECIPE_BUILDER_EDIT_MESSAGE =
  "🔒 This recipe is managed in recipe-builder. Edit it there: the CRM editors would damage it, and the next sync would overwrite the change.";

export function isRecipeBuilderRecipe(recipe) {
  return Boolean(recipe) && recipe.created_by === RECIPE_BUILDER_CREATOR;
}

/**
 * Whether the CRM may edit this recipe's content. Reads the live document when Firestore functions are given, because
 * the recipes table works from `globals/catalog_summary`, which has no `created_by`; falls back to `knownRecipe`.
 * When the document can't be read, editing is blocked rather than risked.
 *
 * @returns {Promise<{allowed: boolean, message: string}>}
 */
export async function checkRecipeEditable(recipeId, { db = null, doc = null, getDoc = null, knownRecipe = null } = {}) {
  if (!recipeId) return { allowed: true, message: "" };
  let recipe = knownRecipe;
  if (db && doc && getDoc) {
    try {
      const snap = await getDoc(doc(db, "recipes", recipeId));
      recipe = snap.exists() ? snap.data() : null;
      if (!recipe) return { allowed: true, message: "" }; // a new document: nothing to protect
    } catch (err) {
      console.error(`❌ [recipeEditGuard] Could not read recipes/${recipeId} to check who manages it:`, err);
      return { allowed: false, message: "⚠️ Couldn't check whether this recipe is managed in recipe-builder, so it wasn't edited. Try again." };
    }
  }
  if (isRecipeBuilderRecipe(recipe)) return { allowed: false, message: RECIPE_BUILDER_EDIT_MESSAGE };
  return { allowed: true, message: "" };
}
